// AGC: SubscriptionAdmin - Admin panel for managing user subscriptions
// AGC: Allows admins to view, edit, and manage user subscription tiers and usage

import React, { useState, useEffect } from 'react'
import { 
  Search, 
  Filter, 
  RefreshCw, 
  Crown, 
  Star, 
  Gift,
  TrendingUp,
  Users,
  DollarSign,
  Calendar,
  CheckCircle,
  XCircle,
  Edit,
  Save,
  X,
} from 'lucide-react'
import { useTranslation } from '../../hooks/useTranslation'
import type { SubscriptionTier, UserSubscription, UsageTracker } from '../../types/subscription'
import { TIER_DISPLAY_INFO, TIER_PRICING } from '../../types/subscription'
import { getBackendUrl } from '../../utils/backendUrl'

const BACKEND_URL = getBackendUrl()

/**
 * AGC: User subscription data with profile info
 */
interface UserSubscriptionData {
  userId: string
  email: string
  displayName: string
  subscription: UserSubscription
  currentUsage?: UsageTracker
  totalHectares?: number
  totalFields?: number
}

/**
 * AGC: Subscription Admin Component
 */
export const SubscriptionAdmin: React.FC = () => {
  const { t } = useTranslation()

  // AGC: State
  const [users, setUsers] = useState<UserSubscriptionData[]>([])
  const [filteredUsers, setFilteredUsers] = useState<UserSubscriptionData[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [tierFilter, setTierFilter] = useState<SubscriptionTier | 'all'>('all')
  const [editingUserId, setEditingUserId] = useState<string | null>(null)
  const [editTier, setEditTier] = useState<SubscriptionTier>('free')

  // AGC: Statistics
  const [stats, setStats] = useState({
    totalUsers: 0,
    freeUsers: 0,
    proUsers: 0,
    premiumUsers: 0,
    totalRevenue: 0,
  })

  /**
   * AGC: Fetch all users with subscriptions
   */
  const fetchUsers = async () => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/subscription/admin/all`)
      const data = await response.json()

      if (data.success) {
        setUsers(data.users)
      } else {
        setError(data.error || 'Erro ao carregar assinaturas')
      }
    } catch (err) {
      console.error('Error fetching subscriptions:', err)
      setError('Erro de conexão')
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * AGC: Update user subscription tier
   */
  const updateUserTier = async (userId: string, newTier: SubscriptionTier) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/subscription`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          tier: newTier,
          billingCycle: 'monthly',
        }),
      })

      const data = await response.json()

      if (data.success) {
        // AGC: Refresh list
        await fetchUsers()
        setEditingUserId(null)
      } else {
        setError(data.error || 'Erro ao atualizar assinatura')
      }
    } catch (err) {
      console.error('Error updating subscription:', err)
      setError('Erro de conexão')
    }
  }

  /**
   * AGC: Filter users based on search and tier
   */
  useEffect(() => {
    let filtered = users

    // AGC: Apply tier filter
    if (tierFilter !== 'all') {
      filtered = filtered.filter(u => u.subscription.tier === tierFilter)
    }

    // AGC: Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(u => 
        u.email.toLowerCase().includes(query) ||
        u.displayName?.toLowerCase().includes(query) ||
        u.userId.toLowerCase().includes(query)
      )
    }

    setFilteredUsers(filtered)
  }, [users, searchQuery, tierFilter])

  /**
   * AGC: Calculate statistics
   */
  useEffect(() => {
    const stats = {
      totalUsers: users.length,
      freeUsers: users.filter(u => u.subscription.tier === 'free').length,
      proUsers: users.filter(u => u.subscription.tier === 'pro').length,
      premiumUsers: users.filter(u => u.subscription.tier === 'premium').length,
      totalRevenue: users.reduce((sum, u) => {
        const pricing = TIER_PRICING[u.subscription.tier]
        return sum + (u.subscription.billingCycle === 'annual' 
          ? pricing.annual 
          : pricing.monthly * 12)
      }, 0),
    }
    setStats(stats)
  }, [users])

  /**
   * AGC: Load data on mount
   */
  useEffect(() => {
    fetchUsers()
  }, [])

  /**
   * AGC: Get tier icon
   */
  const getTierIcon = (tier: SubscriptionTier) => {
    switch (tier) {
      case 'free':
        return <Gift className="w-5 h-5" />
      case 'pro':
        return <Star className="w-5 h-5" />
      case 'premium':
        return <Crown className="w-5 h-5" />
    }
  }

  /**
   * AGC: Get tier color
   */
  const getTierColor = (tier: SubscriptionTier) => {
    const display = TIER_DISPLAY_INFO[tier]
    return display.color
  }

  return (
    <div className="p-6">
      {/* AGC: Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100 mb-2">
          {t('admin.subscriptionManagement') || 'Gerenciamento de Assinaturas'}
        </h1>
        <p className="text-slate-600 dark:text-slate-300">
          {t('admin.subscriptionDescription') || 'Visualize e gerencie assinaturas dos usuários'}
        </p>
      </div>

      {/* AGC: Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white dark:bg-admin-card-dark rounded-lg shadow-card dark:shadow-none p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">{t('admin.totalUsers') || 'Total Usuários'}</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{stats.totalUsers}</p>
            </div>
            <Users className="w-8 h-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-white dark:bg-admin-card-dark rounded-lg shadow-card dark:shadow-none p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">FREE</p>
              <p className="text-2xl font-bold text-emerald-600">{stats.freeUsers}</p>
            </div>
            <Gift className="w-8 h-8 text-emerald-500" />
          </div>
        </div>

        <div className="bg-white dark:bg-admin-card-dark rounded-lg shadow-card dark:shadow-none p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">PRO</p>
              <p className="text-2xl font-bold text-blue-600">{stats.proUsers}</p>
            </div>
            <Star className="w-8 h-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-white dark:bg-admin-card-dark rounded-lg shadow-card dark:shadow-none p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">PREMIUM</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.premiumUsers}</p>
            </div>
            <Crown className="w-8 h-8 text-yellow-500" />
          </div>
        </div>

        <div className="bg-white dark:bg-admin-card-dark rounded-lg shadow-card dark:shadow-none p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">{t('admin.totalRevenue') || 'Receita Anual'}</p>
              <p className="text-2xl font-bold text-green-600">
                R$ {stats.totalRevenue.toLocaleString('pt-BR')}
              </p>
            </div>
            <DollarSign className="w-8 h-8 text-green-500" />
          </div>
        </div>
      </div>

      {/* AGC: Filters and Search */}
      <div className="bg-white dark:bg-admin-card-dark rounded-lg shadow-card dark:shadow-none p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* AGC: Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 dark:text-slate-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('admin.searchUsers') || 'Buscar usuários...'}
              className="w-full pl-10 pr-4 py-2 border border-slate-200 dark:border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-admin-bg-dark dark:text-slate-100"
            />
          </div>

          {/* AGC: Tier Filter */}
          <div className="flex gap-2">
            <button
              onClick={() => setTierFilter('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                tierFilter === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 dark:bg-white/[0.04] text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5'
              }`}
            >
              Todos
            </button>
            <button
              onClick={() => setTierFilter('free')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                tierFilter === 'free'
                  ? 'bg-emerald-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Gift className="w-4 h-4" />
              FREE
            </button>
            <button
              onClick={() => setTierFilter('pro')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                tierFilter === 'pro'
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 dark:bg-white/[0.04] text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5'
              }`}
            >
              <Star className="w-4 h-4" />
              PRO
            </button>
            <button
              onClick={() => setTierFilter('premium')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                tierFilter === 'premium'
                  ? 'bg-yellow-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Crown className="w-4 h-4" />
              PREMIUM
            </button>
          </div>

          {/* AGC: Refresh Button */}
          <button
            onClick={fetchUsers}
            disabled={isLoading}
            className="px-4 py-2 bg-slate-100 dark:bg-white/[0.04] text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5 transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            {t('common.refresh') || 'Atualizar'}
          </button>
        </div>
      </div>

      {/* AGC: Error Message */}
      {error && (
        <div className="bg-error-50 dark:bg-red-500/15 border border-red-200 dark:border-red-500/30 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2 text-error-700 dark:text-red-400">
            <XCircle className="w-5 h-5" />
            <p>{error}</p>
          </div>
          {error.includes('not yet implemented') && (
            <div className="mt-3 p-3 bg-warning-50 dark:bg-yellow-500/15 rounded border border-yellow-200 dark:border-yellow-500/30">
              <p className="text-sm text-warning-700 dark:text-yellow-400">
                <strong>Nota:</strong> O endpoint backend <code>/api/v1/admin/subscriptions</code> precisa ser criado.
                Veja <code>packages/app/backend/routes/subscription.js</code> para implementação.
              </p>
            </div>
          )}
        </div>
      )}

      {/* AGC: Users Table */}
      <div className="bg-white dark:bg-admin-card-dark rounded-lg shadow-card dark:shadow-none overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center">
            <RefreshCw className="w-8 h-8 text-slate-400 dark:text-slate-500 animate-spin mx-auto mb-4" />
            <p className="text-slate-500 dark:text-slate-400">{t('common.loading') || 'Carregando...'}</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="p-12 text-center">
            <Users className="w-12 h-12 text-slate-400 dark:text-slate-500 mx-auto mb-4" />
            <p className="text-slate-500 dark:text-slate-400">
              {searchQuery || tierFilter !== 'all'
                ? (t('admin.noUsersFound') || 'Nenhum usuário encontrado')
                : (t('admin.noUsers') || 'Nenhum usuário com assinatura')}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 dark:bg-white/[0.02]">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t('admin.user') || 'Usuário'}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t('admin.tier') || 'Plano'}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t('admin.status') || 'Status'}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t('admin.startDate') || 'Data Início'}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t('admin.usage') || 'Uso'}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {t('admin.actions') || 'Ações'}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-admin-card-dark divide-y divide-slate-200 dark:divide-white/5">
                {filteredUsers.map((user) => {
                  const display = TIER_DISPLAY_INFO[user.subscription.tier]
                  const isEditing = editingUserId === user.userId

                  return (
                    <tr key={user.userId} className="hover:bg-slate-50/50 dark:hover:bg-white/[0.02]">
                      {/* AGC: User Info */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                            {user.displayName || 'Sem nome'}
                          </p>
                          <p className="text-sm text-slate-500 dark:text-slate-400">{user.email}</p>
                          <p className="text-xs text-slate-400 dark:text-slate-500">{user.userId.substring(0, 8)}...</p>
                        </div>
                      </td>

                      {/* AGC: Tier */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        {isEditing ? (
                          <select
                            value={editTier}
                            onChange={(e) => setEditTier(e.target.value as SubscriptionTier)}
                            className="px-3 py-1 border border-slate-200 dark:border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-admin-bg-dark dark:text-slate-100"
                          >
                            <option value="free">FREE</option>
                            <option value="pro">PRO</option>
                            <option value="premium">PREMIUM</option>
                          </select>
                        ) : (
                          <div className={`flex items-center gap-2 px-3 py-1 rounded-lg ${display.bgColor}`}>
                            <span className={display.color}>{getTierIcon(user.subscription.tier)}</span>
                            <span className={`text-sm font-medium ${display.color}`}>
                              {display.name}
                            </span>
                          </div>
                        )}
                      </td>

                      {/* AGC: Status */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          user.subscription.status === 'active'
                            ? 'bg-success-50 dark:bg-emerald-500/15 text-brand-primary dark:text-emerald-400'
                            : user.subscription.status === 'trial'
                              ? 'bg-info-50 dark:bg-blue-500/15 text-info-700 dark:text-blue-400'
                              : 'bg-slate-100 dark:bg-white/[0.04] text-slate-800 dark:text-slate-300'
                        }`}>
                          {user.subscription.status === 'active' ? 'Ativo' :
                           user.subscription.status === 'trial' ? 'Trial' :
                           'Cancelado'}
                        </span>
                      </td>

                      {/* AGC: Start Date */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 dark:text-slate-400">
                        {user.subscription.startDate
                          ? new Date(user.subscription.startDate).toLocaleDateString('pt-BR')
                          : '-'}
                      </td>

                      {/* AGC: Usage */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.currentUsage ? (
                          <div className="text-sm">
                            <p className="text-slate-900 dark:text-slate-100">
                              {user.currentUsage.tasksCreated} tarefas
                            </p>
                            <p className="text-slate-500 dark:text-slate-400">
                              {user.currentUsage.voiceCommandsUsed} voz
                            </p>
                          </div>
                        ) : (
                          <span className="text-slate-400 dark:text-slate-500">-</span>
                        )}
                      </td>

                      {/* AGC: Actions */}
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {isEditing ? (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => updateUserTier(user.userId, editTier)}
                              className="p-1 text-brand-primary dark:text-emerald-400 hover:bg-success-50 dark:hover:bg-emerald-500/15 rounded"
                              title="Salvar"
                            >
                              <Save className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                setEditingUserId(null)
                                setEditTier('free')
                              }}
                              className="p-1 text-error-700 dark:text-red-400 hover:bg-error-50 dark:hover:bg-red-500/15 rounded"
                              title="Cancelar"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => {
                              setEditingUserId(user.userId)
                              setEditTier(user.subscription.tier)
                            }}
                            className="p-1 text-blue-600 dark:text-blue-400 hover:bg-info-50 dark:hover:bg-blue-500/15 rounded"
                            title="Editar"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default SubscriptionAdmin

